import React from "react";
import { useTranslation } from "react-i18next";
import type { StructureType } from "@/types/silae";

interface StructureSchemaProps {
  type: StructureType;
  className?: string;
}

/**
 * Composant pour afficher les schémas visuels des structures de prime SILAE
 */
export const StructureSchema: React.FC<StructureSchemaProps> = ({
  type,
  className = "",
}) => {
  const { t } = useTranslation();

  const getSchema = () => {
    switch (type) {
      case "simple":
        return (
          <div className={`p-4 bg-muted/30 rounded-lg ${className} h-full`}>
            <h5 className="text-xs font-medium text-muted-foreground mb-3 text-center">
              {t("wizard.structure.schema.flowTitle")}
            </h5>
            <div className="flex flex-col items-center space-y-3">
              {/* Employé */}
              <div className="w-16 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-xs font-medium text-blue-800 dark:text-blue-400">
                {t("wizard.structure.schema.employee")}
              </div>

              {/* Flèche */}
              <div className="flex flex-col items-center">
                <div className="w-px h-4 bg-neutral-300 dark:bg-neutral-600"></div>
                <div className="w-2 h-2 border-r-2 border-b-2 border-neutral-300 dark:border-neutral-600 transform rotate-45"></div>
              </div>

              {/* Prime */}
              <div className="px-4 py-2 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center text-xs font-medium text-green-800 dark:text-green-400">
                {t("wizard.structure.schema.bonus")}
              </div>

              <p className="text-xs text-muted-foreground text-center mt-2">
                {t("wizard.structure.schema.alwaysApplied")}
              </p>
            </div>
          </div>
        );

      case "with_conditions":
        return (
          <div className={`p-4 bg-muted/30 rounded-lg ${className} h-full`}>
            <h5 className="text-xs font-medium text-muted-foreground mb-3 text-center">
              {t("wizard.structure.schema.flowTitle")}
            </h5>
            <div className="flex flex-col items-center space-y-3">
              {/* Employé */}
              <div className="w-16 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-xs font-medium text-blue-800 dark:text-blue-400">
                {t("wizard.structure.schema.employee")}
              </div>

              {/* Flèche vers condition */}
              <div className="flex flex-col items-center">
                <div className="w-px h-4 bg-neutral-300 dark:bg-neutral-600"></div>
                <div className="w-2 h-2 border-r-2 border-b-2 border-neutral-300 dark:border-neutral-600 transform rotate-45"></div>
              </div>

              {/* Condition IF */}
              <div className="w-24 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center text-xs font-medium text-yellow-800 dark:text-yellow-400 text-center">
                {t("wizard.structure.schema.conditions")}
              </div>

              {/* Branches */}
              <div className="flex items-center space-x-8 h-full">
                {/* Branche OUI */}
                <div className="flex flex-col items-center space-y-2">
                  <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                    {t("wizard.structure.schema.yes")}
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-px h-4 bg-neutral-300 dark:bg-neutral-600"></div>
                    <div className="w-2 h-2 border-r-2 border-b-2 border-neutral-300 dark:border-neutral-600 transform rotate-45"></div>
                  </div>
                  <div className="px-4 py-2 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center text-xs font-medium text-green-800 dark:text-green-400 ">
                    {t("wizard.structure.schema.mainBonus")}
                  </div>
                </div>

                {/* Branche NON */}
                <div className="flex flex-col items-center space-y-2 ">
                  <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                    {t("wizard.structure.schema.no")}
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-px h-4 bg-neutral-300 dark:bg-neutral-600"></div>
                    <div className="w-2 h-2 border-r-2 border-b-2 border-neutral-300 dark:border-neutral-600 transform rotate-45"></div>
                  </div>
                  <div className=" w-24 h-12 bg-gray-100 dark:bg-gray-900/30 rounded-lg flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-400">
                    {t("wizard.structure.schema.nothing")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "if_else":
        return (
          <div className={`p-4 bg-muted/30 rounded-lg ${className}`}>
            <h5 className="text-xs font-medium text-muted-foreground mb-3 text-center">
              {t("wizard.structure.schema.flowTitle")}
            </h5>
            <div className="flex flex-col items-center space-y-3">
              {/* Employé */}
              <div className="w-16 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-xs font-medium text-blue-800 dark:text-blue-400">
                {t("wizard.structure.schema.employee")}
              </div>

              {/* Flèche vers condition */}
              <div className="flex flex-col items-center">
                <div className="w-px h-4 bg-neutral-300 dark:bg-neutral-600"></div>
                <div className="w-2 h-2 border-r-2 border-b-2 border-neutral-300 dark:border-neutral-600 transform rotate-45"></div>
              </div>

              {/* Condition IF */}
              <div className="w-24 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center text-xs font-medium text-yellow-800 dark:text-yellow-400 text-center">
                {t("wizard.structure.schema.conditions")}
              </div>

              {/* Branches IF/ELSE */}
              <div className="flex items-center space-x-8">
                {/* Branche IF */}
                <div className="flex flex-col items-center space-y-2">
                  <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                    {t("wizard.structure.schema.yes")}
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-px h-4 bg-neutral-300 dark:bg-neutral-600"></div>
                    <div className="w-2 h-2 border-r-2 border-b-2 border-neutral-300 dark:border-neutral-600 transform rotate-45"></div>
                  </div>
                  <div className="px-4 py-2  bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center text-xs font-medium text-green-800 dark:text-green-400">
                    {t("wizard.structure.schema.mainBonus")}
                  </div>
                </div>

                {/* Branche ELSE */}
                <div className="flex flex-col items-center space-y-2">
                  <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                    {t("wizard.structure.schema.no")}
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-px h-4 bg-neutral-300 dark:bg-neutral-600 "></div>
                    <div className="w-2 h-2 border-r-2 border-b-2 border-neutral-300 dark:border-neutral-600 transform rotate-45"></div>
                  </div>
                  <div className="px-4 py-2  bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center text-xs font-medium text-orange-800 dark:text-orange-400">
                    {t("wizard.structure.schema.alternativeBonus")}
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-2">
                {t("wizard.structure.schema.alwaysBonus")}
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return getSchema();
};
